import React from "react"
import { Controller, useForm } from "react-hook-form"
import { useQuery } from "@apollo/client/react"
import { ActionNodeData, WorkflowNode } from "lib/types/workflow"
import {
  GetCustomStatesDocument,
  GetCustomStatesQuery,
  GetReasonCodesDocument,
  GetReasonCodesQuery
} from "lib/types/generated/graphql"

import Input from "components/Ui/Form/Input"
import SingleSelectSimple from "components/Ui/Form/SingleSelectSimple"
import {
  FormSection,
  FormLabel,
  SelectWrapper,
  SaveButton,
  CheckboxRow
} from "./NodeEditorSidebar.styled"

import { NODE_TYPE_ACTION } from "../nodeKind"

type Props = {
  node: WorkflowNode
  onSave: (nodeId: string, data: ActionNodeData) => void
  hideSave?: boolean
}

interface FormValues {
  label: string
  actionType: string
  reasonCode: string
  customState: string
  deliveryGiftCardProducts: boolean
}


const ACTION_TYPES = [
  { value: "CANCELLATION", label: "Cancellation" },
  { value: "CUSTOM_STATE", label: "Custom State" },
  { value: "DELIVERY", label: "Delivery" }
]

const ActionNodeForm = ({ node, onSave, hideSave = false }: Props) => {
  const data = node.data as ActionNodeData

  const { data: customStatesData, loading: loadingStates } = useQuery<GetCustomStatesQuery>(
    GetCustomStatesDocument
  )
  const { data: reasonCodesData, loading: loadingReasons } = useQuery<GetReasonCodesQuery>(
    GetReasonCodesDocument
  )

  const customStates =
    customStatesData?.Order?.customStates?.states?.filter(
      (s) => s && !s.deleted
    ) ?? []

  const reasonCodes =
    reasonCodesData?.Order?.customReasonCodes?.codes?.filter(Boolean) ?? []

  const reasonOptions = reasonCodes
    .filter((rc): rc is NonNullable<typeof rc> => rc !== null && rc !== undefined)
    .map((rc) => ({
      value: rc.code,
      label: `${rc.label || rc.code} (${rc.code})`
    }))

  const customStateOptions = customStates
    .filter((cs): cs is NonNullable<typeof cs> => cs !== null && cs !== undefined)
    .map((cs) => ({
      value: cs.id,
      label: `${cs.description || cs.id} (${cs.type})`
    }))

  const { register, handleSubmit, watch, control, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      label: data.label,
      actionType: data.actionType || "CANCELLATION",
      reasonCode: data.reason ?? "",
      customState: data.customState ?? "",
      deliveryGiftCardProducts: data.deliveryGiftCardProducts ?? false
    }
  })

  const watchActionType = watch("actionType")

  const onSubmit = (values: FormValues) => {
    const selectedReason = reasonCodes.find((rc) => rc && rc.code === values.reasonCode)
    onSave(node.id, {
      nodeType: NODE_TYPE_ACTION,
      label: values.label,
      actionType: values.actionType,
      reason: values.actionType === "CANCELLATION" ? values.reasonCode : undefined,
      reasonDescription:
        values.actionType === "CANCELLATION" && selectedReason?.label
          ? selectedReason.label
          : undefined,
      customState: values.actionType === "CUSTOM_STATE" ? values.customState : undefined,
      deliveryGiftCardProducts:
        values.actionType === "DELIVERY" ? values.deliveryGiftCardProducts : undefined
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormSection>
        <Input
          name="label"
          label="Name"
          register={register}
          validation={{
            required: "Name is required",
            maxLength: { value: 100, message: "Name must be at most 100 characters" }
          }}
          errors={errors}
        />
      </FormSection>
      <SelectWrapper>
        <FormLabel>Action Type</FormLabel>
        <Controller
          name="actionType"
          control={control}
          render={({ field }) => (
            <SingleSelectSimple
              options={ACTION_TYPES}
              value={ACTION_TYPES.find((at) => at.value === field.value) ?? null}
              setValue={(v: string) => field.onChange(v)}
            />
          )}
        />
      </SelectWrapper>
      {watchActionType === "CANCELLATION" && (
        <SelectWrapper>
          <FormLabel>Cancellation Reason</FormLabel>
          <Controller
            name="reasonCode"
            control={control}
            rules={{ required: "Reason code is required" }}
            render={({ field }) => (
              <SingleSelectSimple
                options={reasonOptions}
                value={reasonOptions.find((o) => o.value === field.value) ?? null}
                setValue={(v: string) => field.onChange(v)}
                placeholder={loadingReasons ? "Loading…" : "Select a reason code"}
              />
            )}
          />
        </SelectWrapper>
      )}
      {watchActionType === "CUSTOM_STATE" && (
        <SelectWrapper>
          <FormLabel>Custom State</FormLabel>
          <Controller
            name="customState"
            control={control}
            rules={{ required: "Custom state is required" }}
            render={({ field }) => (
              <SingleSelectSimple
                options={customStateOptions}
                value={customStateOptions.find((o) => o.value === field.value) ?? null}
                setValue={(v: string) => field.onChange(v)}
                placeholder={loadingStates ? "Loading…" : "Select a custom state"}
              />
            )}
          />
        </SelectWrapper>
      )}
      {watchActionType === "DELIVERY" && (
        <FormSection>
          <FormLabel>Delivery Selection</FormLabel>
          <CheckboxRow>
            <input type="checkbox" {...register("deliveryGiftCardProducts")} />
            Gift card products
          </CheckboxRow>
        </FormSection>
      )}
      {!hideSave && <SaveButton type="submit">Update Node</SaveButton>}
    </form>
  )
}

export default ActionNodeForm
